import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import {
  formatDuration,
  formatViews,
  getMovieById,
  getRelatedMovies,
} from "@/utils/movie";
import NotFound from "@/pages/NotFound";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [toast, setToast] = useState("");
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  useSeo({
    title: movie ? `${movie.title} - 免费在线电影` : "影片详情 - 免费在线电影",
    description: movie
      ? `${movie.title}（${movie.year}）${movie.genres.join("、")}电影，由${movie.director}执导，${movie.cast.join("、")}主演。${movie.synopsis.slice(0, 60)}...`
      : "影片详情介绍。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},免费在线电影,高清电影`
      : "影片详情,免费在线电影",
  });

  if (!movie) return <NotFound />;

  const related = getRelatedMovies(movie, 5);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const metaList = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join("、") },
    { label: "时长", value: formatDuration(movie.duration) },
    { label: "上映日期", value: movie.releaseDate },
    { label: "语言", value: movie.language },
    { label: "制片地区", value: movie.country },
  ];

  return (
    <div className="w-full">
      {/* Backdrop banner */}
      <div className="relative h-[300px] w-full overflow-hidden bg-primary-950 md:h-[420px]">
        <img src={movie.backdrop} alt={`${movie.title} 影片主视觉`} title={movie.title} className="h-full w-full object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/60 to-foreground-950/30"></div>

        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto w-full px-4 pb-6 md:px-6">
            <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-background-100/90" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${movie.genreSlugs[0]}`} className="transition-colors hover:text-accent-400">
                {movie.genres[0]}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-accent-400">{movie.title}</span>
            </nav>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        {/* Main info */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr] lg:-mt-24">
          <div className="mx-auto w-[200px] shrink-0 sm:w-[240px] lg:mx-0 lg:w-full">
            <div className="overflow-hidden rounded-lg border-4 border-background-50 bg-secondary-200 shadow-lg shadow-foreground-950/10">
              <img src={movie.poster} alt={`${movie.title} 电影海报`} title={`${movie.title} 高清电影在线观看`} className="h-full w-full object-top" style={{ aspectRatio: "2 / 3" }} />
            </div>
          </div>

          <div className="lg:pt-28">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{movie.title}</h1>
              <span className="rounded-md bg-accent-500 px-2 py-1 text-sm font-bold text-background-50">{movie.rating.toFixed(1)}</span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <span>{movie.year}</span>
              <span className="flex items-center gap-1"><i className="ri-fire-line text-accent-500"></i>{formatViews(movie.views)} 次观看</span>
              <span>{formatDuration(movie.duration)}</span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((genre, i) => (
                <Link
                  key={genre}
                  to={`/category/${movie.genreSlugs[i]}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-800"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPlayer(true)}
                className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
              >
                <i className="ri-play-circle-fill text-xl"></i>
                立即播放
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 rounded-md border border-background-200 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 whitespace-nowrap"
              >
                <i className="ri-share-forward-line text-lg"></i>
                分享
              </button>
            </div>

            {/* Meta info */}
            <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-2">
              {metaList.map((item) => (
                <div key={item.label} className="flex text-sm">
                  <span className="w-20 shrink-0 text-foreground-500">{item.label}</span>
                  <span className="flex-1 text-foreground-900">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Synopsis */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            剧情简介
          </h2>
          <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-700 md:text-base">{movie.synopsis}</p>
        </section>

        {/* Watch entry */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            在线观看
          </h2>
          <div className="mt-4 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-play-fill text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-800">在线播放功能为前端演示</p>
            <p className="mt-1 max-w-md text-center text-sm text-foreground-500">
              当前页面仅展示播放界面与影片信息，实际播放需后端视频源支持。
            </p>
            <button
              type="button"
              onClick={() => setShowPlayer(true)}
              className="mt-5 rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              演示播放
            </button>
          </div>
        </section>

        {/* Category entrance */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            所属分类
          </h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {movie.genres.map((genre, i) => (
              <Link
                key={genre}
                to={`/category/${movie.genreSlugs[i]}`}
                className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-all hover:-translate-y-0.5 hover:border-accent-300 hover:bg-accent-50"
              >
                <i className="ri-price-tag-3-line text-primary-500"></i>
                更多{genre}电影
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            相关推荐
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>

      {/* Player modal */}
      {showPlayer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/80 px-4 animate-fade-in"
          onClick={() => setShowPlayer(false)}
        >
          <div className="w-full max-w-lg rounded-lg bg-background-50 p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-play-fill text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{movie.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              在线播放功能为前端演示，实际播放需后端支持。当前站点仅提供影片信息浏览与界面演示。
            </p>
            <button
              type="button"
              onClick={() => setShowPlayer(false)}
              className="mt-5 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-in rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}